import {useQuery} from "@tanstack/react-query";
import {useClient} from "../../../utils/hooks";
import {PaginationMeta} from "../../../redux/service/i-service";
import {AxiosError} from "axios";
import {COACH_AVERAGE_RATINGS_QUERY_KEY} from "../constants";
import {GetAverageRatingsApiResponse, ReviewOfferingType} from "../types";

type V2AverageStat = {
  offeringId: number;
  offeringType: string;
  offeringName?: string | null;
  averageRating: number;
  totalReviews: number;
};

const mapV2AverageStatsToResponse = (
  averageStats: V2AverageStat[]
): GetAverageRatingsApiResponse =>
  averageStats.map((stat) => ({
    offering_id: stat.offeringId,
    offering_type: stat.offeringType as ReviewOfferingType,
    avg_rating: String(stat.averageRating),
    rating_count: String(stat.totalReviews),
    offering_name: stat.offeringName ?? null,
  }));

export const useGetAverageRatings = (page: number = 1, per_page: number = 5) => {
  const client = useClient("REVIEWS_AND_RATINGS");

  return useQuery<
    {data: GetAverageRatingsApiResponse; meta: PaginationMeta},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [COACH_AVERAGE_RATINGS_QUERY_KEY, {page}],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        perPage: String(per_page),
      });
      return client(`reviews/average-ratings?${params}`).then((res) => {
        const averageStats: V2AverageStat[] = res.data.averageStats;
        const data = mapV2AverageStatsToResponse(averageStats);
        const meta: PaginationMeta = {
          current_page: page,
          next_page: 0,
          previous_page: 0,
          per_page: per_page,
          total_pages: Math.max(1, Math.ceil(averageStats.length / per_page)),
        };
        return {data, meta};
      });
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
};
